import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import { PaginationConst } from '../constants/AppConstant';
import type { NetWorthSnapshot } from '../../models/NetWorthTypes';

export interface NetWorthSnapshotListResponse {
  result: NetWorthSnapshot[];
  totalCount: number;
  message?: string;
  code: number;
}

/** Coalesce concurrent identical list GETs (e.g. React Strict Mode double mount). */
const inflightList = new Map<string, Promise<NetWorthSnapshotListResponse>>();

function listRequestKey(page: number, size: number): string {
  return `${page}:${size}`;
}

export async function getNetWorthSnapshotsPage(
  page: number = 0,
  size: number = PaginationConst.PAGE_SIZE
): Promise<NetWorthSnapshotListResponse> {
  const key = listRequestKey(page, size);
  const existing = inflightList.get(key);
  if (existing) return existing;

  const p = HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/networth-snapshots`,
    params: { page, size, sort: 'recordDate,desc' },
  })
    .then((response) => response.data as NetWorthSnapshotListResponse)
    .finally(() => {
      inflightList.delete(key);
    });

  inflightList.set(key, p);
  return p;
}

/** Large page for charts, export, and summary cards */
export async function getNetWorthSnapshotsBulk(maxRows: number = 500): Promise<NetWorthSnapshotListResponse> {
  return getNetWorthSnapshotsPage(0, maxRows);
}

type ApiEnvelope<T> = { data: T; message?: string; statusCode: number };

export async function createNetWorthSnapshot(body: NetWorthSnapshot): Promise<ApiEnvelope<NetWorthSnapshot>> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/networth-snapshots`,
    data: body,
  });
  return response.data as ApiEnvelope<NetWorthSnapshot>;
}

export async function updateNetWorthSnapshot(
  id: string,
  body: NetWorthSnapshot
): Promise<ApiEnvelope<NetWorthSnapshot>> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/networth-snapshots/${encodeURIComponent(id)}`,
    data: { ...body, id },
  });
  return response.data as ApiEnvelope<NetWorthSnapshot>;
}

export async function deleteNetWorthSnapshot(id: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/networth-snapshots/${encodeURIComponent(id)}`,
  });
}
